import Typography from '@mui/material/Typography';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import {
    Link as RouterLink,
    useLocation,
} from 'react-router-dom';
import { useAppSelector } from 'src/store';
import HomeIcon from '@mui/icons-material/Home';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useContext } from 'react';
import { CustomThemeContext } from 'src/theme/ThemeProvider';
import { alpha, styled, useTheme } from '@mui/material';
import { translate } from 'src/i18n/tHelper';
import { Box } from 'src/components';

const ModernHomeButton = styled(RouterLink)(
  ({ theme }) => `
    width: 28px;
    height: 28px;
    border-radius: 4px;
    background: transparent;
    border: 1px solid ${alpha(theme.palette.divider, 0.1)};
    transition: all 0.2s ease;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    
    &:hover {
      background: ${alpha(theme.palette.primary.main, 0.08)};
      border-color: ${alpha(theme.palette.primary.main, 0.2)};
      transform: translateY(-1px);
      box-shadow: 0 1px 4px ${alpha(theme.palette.primary.main, 0.15)};
    }
    
    &:active {
      transform: translateY(0);
    }
    
    .MuiSvgIcon-root {
      font-size: 14px;
      transition: all 0.2s ease;
      color: ${theme.palette.text.primary};
    }
    
    &:hover .MuiSvgIcon-root {
      color: ${theme.palette.primary.main};
    }
`
);
const TextBreadcrumbLink = styled(RouterLink)(
    ({ theme }) => `
        display: flex;
        align-items: center;
        text-decoration: none;
        color: ${theme.palette.text.primary};
        font-weight: 500;
        font-size: 0.75rem;
        height: 28px;
        min-height: 28px;
        padding: 0 2px;
        border-radius: 3px;
        background: transparent;
        transition: all 0.2s ease;
        margin: 0;
        white-space: nowrap;
        
        &:hover {
            background: ${alpha(theme.palette.primary.main, 0.08)};
            color: ${theme.palette.primary.main};
            text-decoration: none;
        }
    `
);
const CurrentPageTypography = styled(Typography)(
    ({ theme }) => `
        font-weight: 600;
        font-size: 0.75rem;
        color: ${theme.palette.primary.main};
        background: ${alpha(theme.palette.primary.main, 0.08)};
        padding: 0 4px;
        border-radius: 3px;
        display: flex;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        height: 28px;
        min-height: 28px;
        margin: 0;
    `
);

const RouterBreadcrumbs = () => {
    const location = useLocation();
    const pathnames = location.pathname.split('/').filter((x) => x);
    const { data } = useAppSelector(state => state.menuAuth);
    const theme = useTheme();
    const themeContext = useContext(CustomThemeContext);

    const getBreadcrumbNameMap = (to) => {
        if (to === "/" || to === "/duyuru")
            return translate("pages|menu|breadCrumbs", "Duyurular");
        let menu = data.find(f => f.link === to);
        return translate("pages|menu|breadCrumbs", menu?.yetkiadi);
    }

    return (
        <>
            <Box
                sx={{
                    color: themeContext.mode == "dark" ? "#d0d4d9" : "primary.dark", 
                    fontSize: "1rem",
                    display: { xs: 'block', sm: "block", md: "none", lg: "none", xl: "none" },
                    height: 28,
                    alignItems: 'center'
                }}
            >
                {pathnames.map((value, index) => {
                    const last = index === pathnames.length - 1;
                    let to = `/${pathnames.slice(0, index + 1).join('/')}`;
                    let toLabel = getBreadcrumbNameMap(to);
                    if (!toLabel)
                        return null;
                    return last && (
                        <Typography key={to} 
                            whiteSpace={"nowrap"}
                            sx={{
                                fontWeight: "600", 
                                fontSize: "1.25rem",
                                color: themeContext.mode == "dark" ? "#d0d4d9" : "primary.dark",
                            }}
                        >
                            {getBreadcrumbNameMap(to)}
                        </Typography>
                    )
                })}
            </Box>
            <Breadcrumbs 
                aria-label="breadcrumb"
                separator={<Typography sx={{ 
                    fontSize: '0.75rem', 
                    color: alpha(theme.palette.text.secondary, 0.7),
                    fontWeight: 500,
                    margin: '0 1px'
                }}>
                    /
                </Typography>}
                sx={{
                    width: "100%",
                    height: 28,
                    minHeight: 28,
                    display: { xs: 'none', sm: "none", md: "flex", lg: "flex", xl: "flex" },
                    alignItems: 'center',
                    padding: 0,
                    margin: 0,
                    background: 'transparent',
                    '& .MuiBreadcrumbs-ol': {
                        alignItems: 'center',
                        gap: '0px',
                        height: '100%'
                    },
                    '& .MuiBreadcrumbs-li': {
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                    },
                    '& .MuiBreadcrumbs-separator': {
                        marginLeft: '1px',
                        marginRight: '1px'
                    }
                }}
            >
                <ModernHomeButton to="/duyuru">
                    <HomeIcon />
                </ModernHomeButton>
                
                {pathnames.map((value, index) => {
                    const last = index === pathnames.length - 1;
                    let to = `/${pathnames.slice(0, index + 1).join('/')}`;
                    let toLabel = getBreadcrumbNameMap(to);
                    if (!toLabel)
                        return null;
                        
                    return last ? (
                        <CurrentPageTypography 
                            key={index}
                            whiteSpace="nowrap"
                        >
                            {getBreadcrumbNameMap(to)}
                        </CurrentPageTypography>
                    ) : (
                        <TextBreadcrumbLink
                            key={index}
                            to={to}
                        >
                            {getBreadcrumbNameMap(to)}
                        </TextBreadcrumbLink>
                    );
                })}
            </Breadcrumbs>
        </>
    );
};

export default RouterBreadcrumbs;